import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Truck, ArrowRight, AlertTriangle, Clock, CheckCircle, XCircle,
  Send, Inbox, Search, Filter, RefreshCw, Download, Eye, X, Printer
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRANCHES = ["QC", "PQ", "PM", "CV", "VZ", "OTG-A", "OTG-B", "OTG-C"];
const STATUSES = ["PENDING", "ACCEPTED", "REJECTED", "SENT_ONLY"];

const STATUS_BADGES = {
  PENDING: { bg: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock, label: "PENDING" },
  ACCEPTED: { bg: "bg-green-100 text-green-800 border-green-300", icon: CheckCircle, label: "ACCEPTED" },
  REJECTED: { bg: "bg-red-100 text-red-800 border-red-300", icon: XCircle, label: "REJECTED" },
  SENT_ONLY: { bg: "bg-gray-100 text-gray-700 border-gray-300", icon: Send, label: "SENT (no receipt)" },
};

export default function TransferLog() {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, accepted: 0, rejected: 0, with_mismatch: 0, stale_pending: 0 });
  const [loading, setLoading] = useState(false);
  const [branchFilter, setBranchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [days, setDays] = useState(30);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, limit: 500 };
      if (branchFilter !== "all") params.branch = branchFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const response = await axios.get(`${API}/transfers/log`, { params });
      setTransfers(response.data.transfers || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error("Error fetching transfers:", error);
      toast.error("Failed to load transfers");
    } finally {
      setLoading(false);
    }
  }, [days, branchFilter, statusFilter]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const filteredTransfers = useMemo(() => {
    if (!searchQuery) return transfers;
    const q = searchQuery.toLowerCase();
    return transfers.filter(t =>
      t.item_name?.toLowerCase().includes(q) ||
      t.item_code?.toLowerCase().includes(q) ||
      t.transfer_id?.toLowerCase().includes(q) ||
      t.from_branch?.toLowerCase().includes(q) ||
      t.to_branch?.toLowerCase().includes(q)
    );
  }, [transfers, searchQuery]);

  const exportToExcel = () => {
    const rows = filteredTransfers.map(t => ({
      "Transfer ID": t.transfer_id,
      "Status": t.status,
      "From": t.from_branch,
      "To": t.to_branch,
      "Item": t.item_name,
      "Item Code": t.item_code || "",
      "Qty Sent": t.qty_sent,
      "Qty Received": t.qty_received,
      "Mismatch": t.qty_mismatch ? "YES" : "",
      "Stale (>24h pending)": t.is_stale_pending ? "YES" : "",
      "Sent At": t.sender_side?.created_at || "",
      "Sender Encoder": t.sender_side?.encoder || "",
      "Accepted/Rejected At": t.receiver_side?.accepted_at || t.receiver_side?.rejected_at || "",
      "Receiver Location": t.receiver_side?.location || "",
      "Receiver Encoder": t.receiver_side?.accepted_by || t.receiver_side?.rejected_by || "",
      "Reject Reason": t.receiver_side?.rejected_reason || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transfer Log");
    XLSX.writeFile(wb, `transfer_log_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Exported ${rows.length} transfers`);
  };

  // Generate printable HTML for one or more transfers (transfer slips).
  // Transfers going from the SAME branch to the SAME destination on the SAME
  // DAY are merged into ONE slip with multiple line items, so the user doesn't
  // end up with one piece of paper per item.
  const printTransfers = (transfersToPrint) => {
    if (!transfersToPrint || transfersToPrint.length === 0) {
      toast.error("Walang transfer na pwedeng i-print");
      return;
    }

    // ----- Group by from_branch | to_branch | YYYY-MM-DD -----
    const groups = new Map();
    transfersToPrint.forEach(t => {
      const dateKey = (t.created_at || '').slice(0, 10) || 'unknown';
      const key = `${t.from_branch || '?'}|${t.to_branch || '?'}|${dateKey}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    });

    // Sort groups by date descending (newest first), and items within each group
    // by sent time so the table reads chronologically per day.
    const orderedGroups = Array.from(groups.values()).sort((a, b) => {
      const da = a[0]?.created_at || '';
      const db = b[0]?.created_at || '';
      return db.localeCompare(da);
    });
    orderedGroups.forEach(grp => grp.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')));

    const printedAt = new Date().toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });

    const slipsHtml = orderedGroups.map((group, gIdx) => {
      const first = group[0];
      const fromBranch = first.from_branch || '—';
      const toBranch = first.to_branch || '—';

      // Date header: pretty version of the group date (YYYY-MM-DD)
      const dateOnly = (first.created_at || '').slice(0, 10);
      const prettyDate = dateOnly
        ? new Date(dateOnly + 'T00:00:00').toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })
        : '—';

      // Aggregate totals & statuses for the header meta
      const totalQtySent = group.reduce((s, t) => s + (Number(t.qty_sent) || 0), 0);
      const totalQtyReceived = group.reduce((s, t) => s + (Number(t.qty_received) || 0), 0);
      const hasMismatch = group.some(t => t.qty_mismatch);
      const statuses = Array.from(new Set(group.map(t => t.status)));
      const senderEncoder = first.sender_side?.encoder || '';

      // ----- MERGE SAME-ITEM rows within this group -----
      // If the same item_name + item_code is repeated within one (from/to/date)
      // slip, combine them into a single row with summed quantities so the
      // slip stays compact. Status / time / location are taken from the
      // earliest transfer; mismatch + statuses are unioned.
      const mergedMap = new Map();
      group.forEach(t => {
        const key = `${(t.item_name || '').trim().toLowerCase()}|${(t.item_code || '').trim().toLowerCase()}`;
        if (!mergedMap.has(key)) {
          mergedMap.set(key, {
            item_name: t.item_name || '—',
            item_code: t.item_code || '—',
            qty_sent: 0,
            qty_received: 0,
            sender_locations: new Set(),
            statuses: new Set(),
            mismatch: false,
            earliest_at: t.sender_side?.created_at || t.created_at || '',
            sub_transfers: [],
          });
        }
        const m = mergedMap.get(key);
        m.qty_sent += Number(t.qty_sent) || 0;
        m.qty_received += Number(t.qty_received) || 0;
        if (t.sender_side?.location) m.sender_locations.add(t.sender_side.location);
        if (t.status) m.statuses.add(t.status);
        if (t.qty_mismatch) m.mismatch = true;
        const ts = t.sender_side?.created_at || t.created_at || '';
        if (ts && (!m.earliest_at || ts < m.earliest_at)) m.earliest_at = ts;
        m.sub_transfers.push(t.transfer_id);
      });
      const mergedRows = Array.from(mergedMap.values());

      const headerLabel = group.length > 1
        ? (mergedRows.length < group.length
            ? `BATCH TRANSFER SLIP <span class="batch-pill">${mergedRows.length} item${mergedRows.length > 1 ? 's' : ''} · ${group.length} transfers</span>`
            : `BATCH TRANSFER SLIP <span class="batch-pill">${group.length} items</span>`)
        : 'STOCK TRANSFER SLIP';

      const tidLine = group.length > 1
        ? `Date: <strong>${prettyDate}</strong> &nbsp;|&nbsp; Transfers: <strong>${group.length}</strong>${mergedRows.length < group.length ? ` &nbsp;|&nbsp; Merged into <strong>${mergedRows.length}</strong> item row${mergedRows.length > 1 ? 's' : ''}` : ''}`
        : `Transfer ID: <strong>${first.transfer_id}</strong>`;

      const rows = mergedRows.map((m, i) => {
        const sentTime = m.earliest_at
          ? new Date(m.earliest_at).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' })
          : '—';
        const locs = Array.from(m.sender_locations).join(', ') || '—';
        const sts = Array.from(m.statuses).join(', ');
        const subCountBadge = m.sub_transfers.length > 1
          ? `<span class="sub-count">×${m.sub_transfers.length}</span>`
          : '';
        return `
      <tr>
        <td class="row-num">${i + 1}</td>
        <td>${m.item_name} ${subCountBadge}</td>
        <td>${m.item_code}</td>
        <td class="qty">${m.qty_sent}</td>
        <td class="qty">${m.qty_received || '___'}</td>
        <td class="small">${locs}</td>
        <td class="small">${sentTime}</td>
        <td class="small status-cell">${sts}${m.mismatch ? ' ⚠️' : ''}</td>
      </tr>`;
      }).join('');

      // Collect transfer IDs in a footer for traceability when grouped
      const idList = group.length > 1
        ? `<div class="ids"><strong>Included Transfer IDs:</strong> ${group.map(t => t.transfer_id).join(', ')}</div>`
        : '';

      const isLast = gIdx === orderedGroups.length - 1;

      return `
<div class="slip" style="page-break-after:${isLast ? 'auto' : 'always'};">
  <div class="header">
    <div class="brand">ORION LED LIGHTS</div>
    <div class="title">${headerLabel}</div>
    <div class="tid">${tidLine}</div>
  </div>

  <div class="route">
    <div class="route-box from">
      <div class="route-label">FROM (Sender)</div>
      <div class="route-branch">${fromBranch}</div>
    </div>
    <div class="route-arrow">&#10142;</div>
    <div class="route-box to">
      <div class="route-label">TO (Receiver)</div>
      <div class="route-branch">${toBranch}</div>
    </div>
  </div>

  <table class="items">
    <thead>
      <tr>
        <th>#</th>
        <th>Item Name</th>
        <th>Item Code</th>
        <th>Qty Sent</th>
        <th>Qty Received</th>
        <th>Source Loc.</th>
        <th>Time</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      ${group.length > 1 ? `
      <tr class="totals">
        <td colspan="3" class="totals-label">TOTAL (${mergedRows.length} item row${mergedRows.length > 1 ? 's' : ''} · ${group.length} transfer${group.length > 1 ? 's' : ''})</td>
        <td class="qty">${totalQtySent}</td>
        <td class="qty">${totalQtyReceived}</td>
        <td colspan="3"></td>
      </tr>` : ''}
    </tbody>
  </table>

  <div class="meta">
    <div><strong>Date:</strong> ${prettyDate}</div>
    <div><strong>Status:</strong> ${statuses.join(', ')}${hasMismatch ? ' ⚠️ HAS MISMATCH' : ''}</div>
    ${senderEncoder ? `<div><strong>Prepared By:</strong> ${senderEncoder}</div>` : ''}
    ${idList}
  </div>

  <div class="signatures">
    <div class="sig-block">
      <div class="sig-line"></div>
      <div class="sig-label">SENDER SIGNATURE</div>
      <div class="sig-name">${senderEncoder || ''}</div>
      <div class="sig-date">${prettyDate}</div>
    </div>
    <div class="sig-block">
      <div class="sig-line"></div>
      <div class="sig-label">RECEIVER SIGNATURE</div>
      <div class="sig-date">Date: ____________________</div>
    </div>
  </div>

  <div class="footer">
    <div>Printed: ${printedAt}</div>
    <div>Pakitseck lahat ng items bago pumirma. Isabay 'tong slip sa item shipment.</div>
  </div>
</div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Transfer Slip${orderedGroups.length > 1 ? 's' : ''} — ${orderedGroups.length}</title>
<style>
  @page { size: A4; margin: 1cm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; margin: 0; color: #111; }
  .slip { padding: 16px 24px; max-width: 19cm; margin: 0 auto; }
  .header { text-align: center; border-bottom: 3px solid #111; padding-bottom: 10px; margin-bottom: 16px; }
  .brand { font-size: 22px; font-weight: 900; letter-spacing: 2px; }
  .title { font-size: 16px; font-weight: 700; margin-top: 4px; letter-spacing: 4px; color: #555; }
  .batch-pill { display: inline-block; margin-left: 8px; background: #2563eb; color: white; padding: 2px 8px; border-radius: 999px; font-size: 11px; letter-spacing: 1px; vertical-align: middle; }
  .tid { font-size: 12px; margin-top: 6px; font-family: monospace; }
  .route { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 18px 0; }
  .route-box { flex: 1; padding: 12px; border: 2px solid #333; border-radius: 6px; text-align: center; }
  .route-box.from { background: #eff6ff; border-color: #2563eb; }
  .route-box.to { background: #f0fdf4; border-color: #16a34a; }
  .route-label { font-size: 10px; letter-spacing: 1px; color: #666; margin-bottom: 4px; }
  .route-branch { font-size: 28px; font-weight: 900; }
  .route-arrow { font-size: 28px; color: #666; }
  table.items { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 12px; }
  table.items th, table.items td { border: 1.2px solid #444; padding: 7px 8px; text-align: left; vertical-align: top; }
  table.items th { background: #f3f4f6; font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; }
  table.items td.qty { text-align: center; font-size: 14px; font-weight: 700; }
  table.items td.small { font-size: 10.5px; color: #444; }
  table.items td.row-num { text-align: center; font-weight: 600; color: #555; width: 28px; }
  table.items tr.totals td { background: #fffbeb; font-weight: 700; }
  table.items tr.totals td.totals-label { text-align: right; letter-spacing: 1px; font-size: 11px; }
  .status-cell { text-transform: uppercase; font-size: 9.5px; }
  .sub-count { display: inline-block; margin-left: 6px; background: #fef3c7; color: #92400e; padding: 1px 6px; border-radius: 999px; font-size: 10px; font-weight: 700; vertical-align: middle; }
  .meta { background: #fafafa; border: 1px solid #ddd; padding: 10px 14px; border-radius: 4px; font-size: 12px; margin-bottom: 22px; }
  .meta div { margin: 3px 0; }
  .meta .ids { font-size: 10px; color: #555; word-break: break-all; margin-top: 6px; padding-top: 6px; border-top: 1px dashed #ccc; }
  .signatures { display: flex; gap: 24px; margin-top: 50px; }
  .sig-block { flex: 1; text-align: center; }
  .sig-line { border-top: 1.5px solid #111; height: 1px; margin-bottom: 4px; }
  .sig-label { font-size: 10px; letter-spacing: 1.5px; color: #555; }
  .sig-name { font-weight: 600; margin-top: 4px; font-size: 12px; }
  .sig-date { font-size: 10px; color: #666; margin-top: 2px; }
  .footer { margin-top: 24px; padding-top: 8px; border-top: 1px dashed #999; text-align: center; font-size: 10px; color: #666; }
  @media print { .slip { padding: 0; } }
</style>
</head>
<body>
${slipsHtml}
<script>
  window.addEventListener('load', function() {
    setTimeout(function() { window.print(); }, 250);
  });
</script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      toast.error("Nakablock ang popup. Allow popups para makapag-print.");
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    toast.success(orderedGroups.length === transfersToPrint.length
      ? `Naka-prep ${orderedGroups.length} slip${orderedGroups.length > 1 ? 's' : ''}`
      : `Pinagsama ang ${transfersToPrint.length} transfers into ${orderedGroups.length} slip${orderedGroups.length > 1 ? 's' : ''} (per date / route)`
    );
  };

  const printAllVisible = () => {
    if (filteredTransfers.length > 50) {
      if (!window.confirm(`I-print ang ${filteredTransfers.length} transfer slips? Marami 'to — baka mas maganda mag-filter muna.`)) {
        return;
      }
    }
    printTransfers(filteredTransfers);
  };

  // Today's outgoing manifest — print/filter all transfers created today,
  // across ALL source branches and destinations. Uses local-time date match.
  const todaysTransfers = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return filteredTransfers.filter(t => (t.created_at || '').slice(0, 10) === today);
  }, [filteredTransfers]);

  const printTodayManifest = () => {
    if (todaysTransfers.length === 0) {
      toast.info("Walang outgoing transfers para sa araw na 'to");
      return;
    }
    printTransfers(todaysTransfers);
  };

  return (
    <ErrorBoundary onReset={() => fetchTransfers()}>
      <div className="min-h-screen bg-gray-50 p-6" data-testid="transfer-log-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Truck className="h-7 w-7 text-blue-600" />
                Transfer Log
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Two-side visibility: makikita mo BOTH yung ipinadala at yung tinanggap para double-check.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={fetchTransfers}
                disabled={loading}
                data-testid="refresh-transfers-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                onClick={printTodayManifest}
                disabled={todaysTransfers.length === 0}
                className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60"
                data-testid="print-today-manifest-btn"
                title="I-print lahat ng outgoing transfers para sa araw na 'to, kahit anong branch"
              >
                <Truck className="h-4 w-4 mr-2" />
                Today's Manifest ({todaysTransfers.length})
              </Button>
              <Button
                variant="outline"
                onClick={printAllVisible}
                disabled={filteredTransfers.length === 0}
                className="border-blue-500 text-blue-700 hover:bg-blue-50"
                data-testid="print-all-transfers-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All ({filteredTransfers.length})
              </Button>
              <Button
                onClick={exportToExcel}
                className="bg-green-600 hover:bg-green-700"
                disabled={filteredTransfers.length === 0}
                data-testid="export-transfers-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <SummaryCard label="Total" value={summary.total} color="blue" />
          <SummaryCard label="Pending" value={summary.pending} color="amber" />
          <SummaryCard label="Accepted" value={summary.accepted} color="green" />
          <SummaryCard label="Rejected" value={summary.rejected} color="red" />
          <SummaryCard label="Mismatch" value={summary.with_mismatch} color="orange" warn />
          <SummaryCard label="Stale (>24h)" value={summary.stale_pending} color="rose" warn />
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search item, branch, or transfer ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-transfer-input"
                  />
                </div>
              </div>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="w-[140px]" data-testid="branch-filter-select">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {BRANCHES.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter-select">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
                <SelectTrigger className="w-[120px]" data-testid="days-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last 365 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Transfer Cards */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin" />
            Loading transfers...
          </div>
        ) : filteredTransfers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Truck className="h-16 w-16 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-semibold">Walang transfers na nakita</p>
              <p className="text-sm text-gray-400">Try adjusting the filters above.</p>
            </CardContent>
          </Card>
        ) : (() => {
          // Group transfers by sender branch → destination branch → date desc
          // Layout:
          //   From PQ
          //     → To VZ
          //         📅 2026-03-10  [Print 2 transfers]
          //         📅 2026-03-09  [Print 1 transfer]
          //     → To CV
          //         📅 2026-03-10  [Print 1 transfer]
          const grouped = {};
          filteredTransfers.forEach(t => {
            const from = t.from_branch || 'Unknown';
            const to = t.to_branch || 'Unknown';
            let dateKey = 'Unknown';
            try {
              if (t.created_at) dateKey = new Date(t.created_at).toISOString().split('T')[0];
            } catch (e) { /* ignore */ }
            if (!grouped[from]) grouped[from] = {};
            if (!grouped[from][to]) grouped[from][to] = {};
            if (!grouped[from][to][dateKey]) grouped[from][to][dateKey] = [];
            grouped[from][to][dateKey].push(t);
          });
          
          const sortedBranches = Object.keys(grouped).sort();
          
          return (
            <div className="space-y-4">
              <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900">
                📊 <strong>{filteredTransfers.length}</strong> transfers across <strong>{sortedBranches.length}</strong> source branch(es)
              </div>
              
              {sortedBranches.map(fromBranch => {
                const destinations = grouped[fromBranch];
                const sortedDestinations = Object.keys(destinations).sort();
                const branchTransfers = sortedDestinations.flatMap(d => Object.values(destinations[d]).flat());
                const branchMismatches = branchTransfers.filter(t => t.qty_mismatch).length;
                const branchStale = branchTransfers.filter(t => t.is_stale_pending).length;
                
                return (
                  <div key={fromBranch} className="border-2 border-blue-300 rounded-lg overflow-hidden bg-white">
                    {/* Branch Header with Print Group button */}
                    <div className="bg-gradient-to-r from-blue-100 to-blue-50 px-4 py-3 border-b-2 border-blue-300 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-bold text-blue-900 flex items-center gap-2 text-lg">
                          <Send className="h-5 w-5" />
                          From {fromBranch}
                        </div>
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-semibold">
                          {branchTransfers.length} transfer{branchTransfers.length > 1 ? 's' : ''}
                        </span>
                        <span className="text-xs bg-indigo-500 text-white px-2 py-0.5 rounded-full font-semibold">
                          → {sortedDestinations.length} destination{sortedDestinations.length > 1 ? 's' : ''}
                        </span>
                        {branchMismatches > 0 && (
                          <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {branchMismatches} mismatch
                          </span>
                        )}
                        {branchStale > 0 && (
                          <span className="text-xs bg-rose-500 text-white px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {branchStale} stale
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => printTransfers(branchTransfers)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`print-branch-group-${fromBranch}`}
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print All from {fromBranch} ({branchTransfers.length})
                      </Button>
                    </div>
                    
                    {/* Per-destination sub-groups */}
                    {sortedDestinations.map(toBranch => {
                      const destDates = destinations[toBranch];
                      const sortedDestDates = Object.keys(destDates).sort((a, b) => b.localeCompare(a));
                      const destTransfers = sortedDestDates.flatMap(d => destDates[d]);
                      
                      return (
                        <div key={`${fromBranch}-${toBranch}`} className="border-b-2 border-blue-200 last:border-b-0 bg-slate-50/40">
                          {/* Destination header */}
                          <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-200 flex items-center justify-between flex-wrap gap-2" data-testid={`dest-group-${fromBranch}-to-${toBranch}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <ArrowRight className="h-4 w-4 text-emerald-700" />
                              <span className="font-semibold text-emerald-900">To {toBranch}</span>
                              <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full font-semibold">
                                {destTransfers.length} transfer{destTransfers.length > 1 ? 's' : ''}
                              </span>
                              <span className="text-xs text-emerald-700">
                                across {sortedDestDates.length} day{sortedDestDates.length > 1 ? 's' : ''}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => printTransfers(destTransfers)}
                              className="border-emerald-500 text-emerald-800 hover:bg-emerald-100 h-8"
                              data-testid={`print-dest-group-${fromBranch}-to-${toBranch}`}
                            >
                              <Printer className="h-3.5 w-3.5 mr-1" />
                              Print {fromBranch} → {toBranch} ({destTransfers.length})
                            </Button>
                          </div>
                          
                          {/* Per-date sub-groups within destination — each
                              date prints as its OWN slip; never combined with
                              other dates. Per-date Print button is always
                              shown for clarity. */}
                          {sortedDestDates.map(date => (
                            <div key={`${fromBranch}-${toBranch}-${date}`} className="border-b border-slate-200 last:border-b-0">
                              <div className="bg-white px-4 py-1.5 border-b border-slate-100 flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-700">📅 {date}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">{destDates[date].length} transfer(s)</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => printTransfers(destDates[date])}
                                    className="h-6 px-2 text-xs text-emerald-700 hover:bg-emerald-50"
                                    data-testid={`print-date-${fromBranch}-${toBranch}-${date}`}
                                    title={`I-print ang ${destDates[date].length} transfer${destDates[date].length > 1 ? 's' : ''} ng ${date} lang`}
                                  >
                                    <Printer className="h-3 w-3 mr-1" />
                                    Print {date}
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-2 p-3">
                                {destDates[date].map(t => (
                                  <TransferCard
                                    key={t.transfer_id}
                                    transfer={t}
                                    onView={() => setSelectedTransfer(t)}
                                    onPrint={() => printTransfers([t])}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Detail Modal */}
        <Dialog open={!!selectedTransfer} onOpenChange={(open) => !open && setSelectedTransfer(null)}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="transfer-detail-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Transfer Detail
              </DialogTitle>
              <DialogDescription>
                Both-side view ng transfer record. Compare details para mahuli ang anomaly.
              </DialogDescription>
            </DialogHeader>
            {selectedTransfer && <TransferDetail transfer={selectedTransfer} />}
            <DialogFooter>
              {selectedTransfer && (
                <Button
                  variant="outline"
                  onClick={() => printTransfers([selectedTransfer])}
                  className="border-blue-500 text-blue-700 hover:bg-blue-50"
                  data-testid="print-from-detail-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print Slip
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelectedTransfer(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
}

function SummaryCard({ label, value, color, warn }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    green: "bg-green-50 text-green-700 border-green-200",
    red: "bg-red-50 text-red-700 border-red-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <div className={`p-3 rounded-lg border ${colorMap[color]} ${warn && value > 0 ? "ring-2 ring-offset-1 ring-current" : ""}`}>
      <div className="text-xs font-medium uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function TransferCard({ transfer, onView, onPrint }) {
  const status = STATUS_BADGES[transfer.status] || STATUS_BADGES.SENT_ONLY;
  const StatusIcon = status.icon;
  return (
    <Card className={`hover:shadow-md transition-shadow ${transfer.qty_mismatch ? "border-orange-400 border-2" : ""} ${transfer.is_stale_pending ? "border-rose-400 border-2" : ""}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold border inline-flex items-center gap-1 ${status.bg}`}>
                <StatusIcon className="h-3 w-3" />
                {status.label}
              </span>
              {transfer.qty_mismatch && (
                <span className="px-2 py-0.5 rounded text-xs font-semibold border bg-orange-100 text-orange-800 border-orange-300 inline-flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  QTY MISMATCH ({transfer.qty_sent} sent vs {transfer.qty_received} received)
                </span>
              )}
              {transfer.is_stale_pending && (
                <span className="px-2 py-0.5 rounded text-xs font-semibold border bg-rose-100 text-rose-800 border-rose-300 inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  STALE (&gt;24h pending)
                </span>
              )}
              <span className="text-xs text-gray-400 font-mono">{transfer.transfer_id}</span>
            </div>
            
            <div className="font-semibold text-gray-900 mb-1">{transfer.item_name}</div>
            
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded">
                <Send className="h-3 w-3 text-blue-600" />
                <span className="font-semibold text-blue-800">{transfer.from_branch}</span>
                <span className="text-gray-500">sent</span>
                <span className="font-bold text-blue-900">{transfer.qty_sent}</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded">
                <Inbox className="h-3 w-3 text-green-600" />
                <span className="font-semibold text-green-800">{transfer.to_branch}</span>
                <span className="text-gray-500">{transfer.status === "ACCEPTED" ? "received" : "pending"}</span>
                <span className="font-bold text-green-900">{transfer.qty_received}</span>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 mt-2">
              {transfer.sender_side?.created_at && (
                <span>Sent: {new Date(transfer.sender_side.created_at).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}</span>
              )}
              {transfer.sender_side?.encoder && <span> by {transfer.sender_side.encoder}</span>}
            </div>
          </div>
          
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={onView}
              data-testid={`view-transfer-${transfer.transfer_id}`}
            >
              <Eye className="h-4 w-4 mr-1" />
              Details
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onPrint}
              className="border-blue-500 text-blue-700 hover:bg-blue-50"
              data-testid={`print-transfer-${transfer.transfer_id}`}
            >
              <Printer className="h-4 w-4 mr-1" />
              Print Slip
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TransferDetail({ transfer }) {
  const status = STATUS_BADGES[transfer.status] || STATUS_BADGES.SENT_ONLY;
  return (
    <div className="space-y-4 py-2">
      {/* Status Header */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div>
          <div className="text-xs text-gray-500 font-mono">{transfer.transfer_id}</div>
          <div className="font-semibold text-lg">{transfer.item_name}</div>
        </div>
        <span className={`px-3 py-1.5 rounded text-sm font-semibold border ${status.bg}`}>
          {status.label}
        </span>
      </div>

      {/* Mismatch / Stale alerts */}
      {(transfer.qty_mismatch || transfer.is_stale_pending) && (
        <div className="p-3 bg-orange-50 border-2 border-orange-300 rounded-lg">
          <div className="flex items-center gap-2 text-orange-800 font-semibold mb-1">
            <AlertTriangle className="h-4 w-4" />
            Anomaly Detected
          </div>
          {transfer.qty_mismatch && (
            <div className="text-sm text-orange-700">
              ⚠️ Quantity mismatch: <strong>{transfer.qty_sent}</strong> sent pero <strong>{transfer.qty_received}</strong> lang ang tinanggap. Verify with both branches.
            </div>
          )}
          {transfer.is_stale_pending && (
            <div className="text-sm text-orange-700">
              🕒 Pending na ng over 24 hours. Ipa-check sa receiver kung tinanggap na o nawawala sa transit.
            </div>
          )}
        </div>
      )}

      {/* Two-side panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Sender side */}
        <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-center gap-2 text-blue-800 font-semibold mb-3">
            <Send className="h-4 w-4" />
            Sender Side ({transfer.from_branch})
          </div>
          {transfer.sender_side ? (
            <dl className="space-y-1 text-sm">
              <DetailRow label="Branch" value={transfer.sender_side.branch} />
              <DetailRow label="Item Code" value={transfer.sender_side.item_code || "—"} />
              <DetailRow label="Quantity" value={String(transfer.sender_side.quantity)} highlight />
              <DetailRow label="Source Location" value={transfer.sender_side.location || "—"} />
              <DetailRow label="Encoder" value={transfer.sender_side.encoder || "—"} />
              <DetailRow label="Sent At" value={transfer.sender_side.created_at ? new Date(transfer.sender_side.created_at).toLocaleString('en-PH') : "—"} />
              {transfer.sender_side.notes && (
                <DetailRow label="Notes" value={transfer.sender_side.notes} />
              )}
            </dl>
          ) : (
            <div className="text-sm text-gray-500 italic">No sender record found.</div>
          )}
        </div>

        {/* Receiver side */}
        <div className="p-4 border-2 border-green-200 rounded-lg bg-green-50">
          <div className="flex items-center gap-2 text-green-800 font-semibold mb-3">
            <Inbox className="h-4 w-4" />
            Receiver Side ({transfer.to_branch})
          </div>
          {transfer.receiver_side ? (
            <dl className="space-y-1 text-sm">
              <DetailRow label="Branch" value={transfer.receiver_side.branch} />
              <DetailRow label="Quantity Received" value={String(transfer.receiver_side.quantity)} highlight />
              <DetailRow label="Status" value={transfer.receiver_side.transfer_status || transfer.status} />
              <DetailRow label="Receiving Location" value={transfer.receiver_side.location || "—"} />
              <DetailRow label="Accepted By" value={transfer.receiver_side.accepted_by || "—"} />
              <DetailRow label="Accepted At" value={transfer.receiver_side.accepted_at ? new Date(transfer.receiver_side.accepted_at).toLocaleString('en-PH') : "—"} />
              {transfer.receiver_side.rejected_by && (
                <DetailRow label="Rejected By" value={transfer.receiver_side.rejected_by} />
              )}
              {transfer.receiver_side.rejected_reason && (
                <DetailRow label="Reject Reason" value={transfer.receiver_side.rejected_reason} />
              )}
            </dl>
          ) : (
            <div className="text-sm text-gray-500 italic">No receiver record found (sent but not yet received or transferred outside the system).</div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between gap-2 py-0.5">
      <dt className="text-gray-600 text-xs uppercase tracking-wide">{label}</dt>
      <dd className={`text-right ${highlight ? "font-bold text-base" : ""}`}>{value}</dd>
    </div>
  );
}
